import { useEffect, useState } from "react";

import { TextInput } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useInterval } from "@mantine/hooks";

import type { QuestionnaireFormWithEditFlag } from "@/intake-forms/types/questionnaire-form";
import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import {
  clampElapsedTimerDurationSeconds,
  formatSecondsAsHoursMinutesSeconds,
  parseHoursMinutesSecondsToSeconds,
} from "@/utils/hours-minutes-seconds-duration";
import { GenericFormValues } from "@/utils/questionnaires";

interface TimerInputProps<T> {
  /** Dotted form path (matches Mantine `setFieldValue` / `getInputProps`). */
  readonly formFieldPath: string;
  readonly label: string;
  readonly form: UseFormReturnType<T>;
}
function TimerInput<T extends GenericFormValues>({
  formFieldPath,
  label,
  form,
}: TimerInputProps<T>) {
  const isEditingQuestionnaire = Boolean(
    (form as QuestionnaireFormWithEditFlag<T>).isEditingQuestionnaire,
  );
  const inputProps = form.getInputProps(formFieldPath);

  const [seconds, setSeconds] = useState(
    () => parseHoursMinutesSecondsToSeconds(inputProps.value) ?? 0,
  );

  useEffect(() => {
    if (isEditingQuestionnaire) return;
    const parsed = parseHoursMinutesSecondsToSeconds(inputProps.value);
    if (parsed !== null) setSeconds(parsed);
  }, [inputProps.value, isEditingQuestionnaire]);

  const interval = useInterval(() => {
    setSeconds((previousSeconds) => {
      const newSeconds = clampElapsedTimerDurationSeconds(previousSeconds + 1);
      if (newSeconds === previousSeconds) {
        return previousSeconds;
      }
      queueMicrotask(() => {
        setFormFieldValue(form, formFieldPath, formatSecondsAsHoursMinutesSeconds(newSeconds));
      });
      return newSeconds;
    });
  }, 1000);

  useEffect(() => {
    if (isEditingQuestionnaire) {
      interval.stop();
      return;
    }

    interval.start();
    return () => {
      interval.stop();
    };
  }, [form, formFieldPath, isEditingQuestionnaire, interval]);

  if (isEditingQuestionnaire) {
    return <TextInput label={label} {...inputProps} placeholder="HH:mm:ss" />;
  }

  return <TextInput readOnly label={label} value={formatSecondsAsHoursMinutesSeconds(seconds)} />;
}

export default TimerInput;
